import { useMemo, useState } from 'react';
import { useLang } from 'hooks/lang';

import { Modal, Button, LabeledSelect } from 'components/Blocks';

import { CirclePlusIcon } from 'components/Icons';

import { PackageInvoiceValue } from './InvoiceValue';
import { CreateInvoiceModal } from 'pages/Invoice/components';

import { useUser } from 'hooks/user';
import { useFormControl } from 'hooks';

import { canCreateInvoice, isInvoiceValueOk } from '../../utils';
import { INVOICE_COUNTRIES } from 'utils/countries';

import { usePackage } from '../../context';

export function CreatePackageInvoice(): JSX.Element | null {
  const user = useUser();
  const { t } = useLang();
  const { data, reload } = usePackage();

  const [stage, setStage] = useState<null | 'PRE' | 'CREATION'>(null);

  const {
    state: preCreationProps,
    dispatch,
    resetDefault,
    handleChange,
  } = useFormControl({
    country: '',
    value: '',
  });

  const closeAll = (): void => {
    setStage(null);
    resetDefault();
  };

  const canCreate = canCreateInvoice(data, user);
  const selectedValueIsOk = stage === 'PRE' ? isInvoiceValueOk(data, preCreationProps.value) : true;

  const packageEntities = useMemo(
    () => [{ ...data, ...preCreationProps }],
    [data, preCreationProps],
  );

  if (!canCreate) return null;

  return (
    <>
      <CreateInvoiceModal
        close={closeAll}
        entities={stage === 'CREATION' ? packageEntities : []}
        onCreateSuccess={reload}
        payloadGenerator={() => ({
          type: 'PACKAGE',
          entries: [{ id: data.id, ...preCreationProps, type: 'PACKAGE' }],
        })}
      />

      <Modal
        isOpen={stage === 'PRE'}
        close={closeAll}
        title={t('invoice-data') as string}
        width="min(80vw, 550px)"
        gap="6"
      >
        <LabeledSelect
          label={t('country')}
          id="country"
          options={INVOICE_COUNTRIES}
          value={preCreationProps.country}
          onChange={handleChange}
        />

        <PackageInvoiceValue state={preCreationProps} dispatch={dispatch} data={data} />

        <Button
          disabled={Object.values(preCreationProps).some((x) => !x) || !selectedValueIsOk}
          onClick={() => setStage('CREATION')}
        >
          {t('continue')}
        </Button>
      </Modal>

      <Button variant="ghost" onClick={() => setStage('PRE')}>
        <CirclePlusIcon size={16} />
        <span className="phone:hidden">{t('create')}</span>
      </Button>
    </>
  );
}
